
import { useMemo, useReducer } from "react";
import {
    BuilderFieldDef,
    BuilderFieldRules,
    BuilderStep,
    FormBuilderInitialMetadata,
    FormBuilderState,
    PrmFieldType,
} from "../types/formBuilder.types";
import { parseFormHtml } from "../services/htmlParser";
import { serializeFormHtml } from "../services/htmlSerializer";
import { builderFieldTypeLabelKey, getFieldTypeDefinition } from "../components/shared/FieldTypeRegistry";
import { useTranslation } from "react-i18next";
import { newTranslationId } from "../services/translationIdentity";

const defaultRules = (): BuilderFieldRules => ({ visible: "", required: "", readonly: "", hidden: "", disabled: "", label: "" });

export const DEFAULT_REQUEST_STATUS_MAPPING = JSON.stringify({
    open: ["new", "open", "todo"],
    in_progress: ["in_progress", "doing"],
    closed: ["closed", "resolved", "done"],
    cancelled: ["cancelled", "canceled"],
}, null, 2);

export const DEFAULT_FORM_SCRIPT_CODE = `({
  data() {
    return {
      // To enable these handlers, add prm:events="formEvents" to <prm-form>.
      formEvents: {
        /** @param {ServicePortalFormApi} form */
        onLoad(form) {
          // Configure the form after it loads.
        },
        /** @param {ServicePortalFormApi} form @param {ServicePortalFormChangeEvent} event */
        onChange(form, event) {
          if (event.field === "field_name") {
            // React to event.value and event.previousValue.
          }
        },
        /** @param {ServicePortalFormApi} form @param {ServicePortalFormSubmitEvent} event */
        onSubmit(form, event) {
          // Return false to cancel submit.
          return true;
        },
        /** @param {ServicePortalFormApi} form @param {ServicePortalFormSuccessEvent} event */
        onSuccess(form, event) {
          // event.record is the created or updated record response.
        },
        /** @param {ServicePortalFormApi} form @param {ServicePortalFormErrorEvent} event */
        onError(form, event) {
          Gate.ui.notify(event.error?.message || "Form submission failed", "error");
        }
      }
    };
  },
  mounted() {
    if (Gate.form) {
      // Gate.form is available after prm-form is mounted.
    }
  }
})`;

export const normalizeBuilderToken = (value: string, separator: "_" | "-" = "_"): string => {
    const normalized = String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, separator)
        .replace(new RegExp("^" + separator + "+|" + separator + "+$", "g"), "");

    return normalized || "service_form";
};

const uniqueName = (base: string, existingNames: string[], currentName?: string): string => {
    const used = new Set(existingNames.filter((name) => name && name !== currentName));
    let candidate = base;
    let counter = 2;

    while (used.has(candidate)) {
        candidate = base + "_" + counter;
        counter += 1;
    }

    return candidate;
};

const isTranslationKey = (value: string): boolean => /^[a-z][a-z0-9_-]*(\.[a-z0-9_-]+)+$/i.test(String(value || "").trim());

const fieldBaseName = (label: string, type: PrmFieldType): string => {
    if (label && !isTranslationKey(label)) {
        return normalizeBuilderToken(label, "_");
    }

    return normalizeBuilderToken(getFieldTypeDefinition(type)?.label || type, "_");
};

const applyMetadata = (state: FormBuilderState, metadata?: FormBuilderInitialMetadata): FormBuilderState => ({
    ...state,
    formTitle: metadata?.formTitle || state.formTitle,
    formName: metadata?.formName || state.formName,
    formSlug: metadata?.formSlug || state.formSlug,
    identityLocked: Boolean(metadata?.formName || metadata?.formSlug),
    styleCode: metadata?.styleCode ?? state.styleCode,
    categoryId: metadata?.categoryId ?? state.categoryId,
    accessType: metadata?.accessType || state.accessType,
    accessRoles: metadata?.accessRoles ?? state.accessRoles,
    accessGroupIds: metadata?.accessGroupIds ?? state.accessGroupIds,
    publicAccessMode: metadata?.publicAccessMode ?? state.publicAccessMode,
    active: metadata?.active ?? state.active,
    published: metadata?.published ?? state.published,
    version: metadata?.version ?? state.version,
    requestStatusField: metadata?.requestStatusField ?? state.requestStatusField,
    requestStatusMappingJson: metadata?.requestStatusMappingJson ?? state.requestStatusMappingJson,
});

const createInitialState = (initialHtml?: string, initialScriptCode = "", metadata?: FormBuilderInitialMetadata): FormBuilderState => {
    const baseTitle = metadata?.formTitle || "New Service Form";
    const baseState: FormBuilderState = {
        translationId: metadata?.translationId || newTranslationId(),
        previewTranslations: {},
        formTitle: baseTitle,
        formName: metadata?.formName || normalizeBuilderToken(baseTitle, "_"),
        formSlug: metadata?.formSlug || normalizeBuilderToken(baseTitle, "-"),
        identityLocked: Boolean(metadata?.formName || metadata?.formSlug),
        styleCode: metadata?.styleCode || "",
        categoryId: metadata?.categoryId || "",
        accessType: metadata?.accessType || "authenticated",
        accessRoles: metadata?.accessRoles || "",
        accessGroupIds: metadata?.accessGroupIds || [],
        publicAccessMode: metadata?.publicAccessMode || "both",
        active: metadata?.active ?? true,
        published: metadata?.published ?? false,
        version: metadata?.version ?? 1,
        requestStatusField: metadata?.requestStatusField || "",
        requestStatusMappingJson: metadata?.requestStatusMappingJson || DEFAULT_REQUEST_STATUS_MAPPING,
        targetTable: "",
        onSubmit: "create",
        successMessage: "",
        successRedirect: "",
        recordId: "",
        eventsBinding: "",
        fields: [],
        steps: [],
        sections: [],
        selectedField: null,
        activeStepIndex: null,
        activeView: "nocode",
        htmlSource: "",
        htmlDirty: false,
        scriptCode: initialScriptCode || DEFAULT_FORM_SCRIPT_CODE,
    };

    if (!initialHtml) {
        return baseState;
    }

    const parsed = parseFormHtml(initialHtml);
    return applyMetadata({
        ...baseState,
        translationId: parsed.translationId || baseState.translationId,
        targetTable: parsed.targetTable || "",
        onSubmit: parsed.onSubmit || "create",
        successMessage: parsed.successMessage || "",
        successRedirect: parsed.successRedirect || "",
        recordId: parsed.recordId || "",
        eventsBinding: parsed.eventsBinding || "",
        fields: parsed.fields || [],
        steps: parsed.steps || [],
        sections: parsed.sections || [],
        activeStepIndex: parsed.steps?.length ? 0 : null,
        htmlSource: initialHtml,
        htmlDirty: false,
    }, metadata);
};

type FormBuilderAction =
    | { type: "ADD_FIELD"; fieldType: PrmFieldType; label?: string; index?: number; stepIndex?: number }
    | { type: "REMOVE_FIELD"; name: string }
    | { type: "MOVE_FIELD"; fromIndex: number; toIndex: number; stepIndex?: number | null }
    | { type: "UPDATE_FIELD"; name: string; updates: Partial<BuilderFieldDef>; labelText?: string }
    | { type: "UPDATE_FIELD_RULES"; name: string; rules: Partial<BuilderFieldRules> }
    | { type: "SELECT_FIELD"; name: string | null }
    | { type: "SET_ACTIVE_STEP"; stepIndex: number | null }
    | { type: "SET_VIEW"; view: "nocode" | "developer" }
    | { type: "SET_HTML"; html: string }
    | { type: "SET_SCRIPT_CODE"; scriptCode: string }
    | { type: "SET_PREVIEW_TRANSLATION"; key: string; text: string }
    | { type: "SET_FORM_PROPS"; props: Partial<Pick<FormBuilderState, "targetTable" | "onSubmit" | "successMessage" | "successRedirect" | "recordId" | "eventsBinding" | "categoryId" | "accessType" | "accessRoles" | "accessGroupIds" | "publicAccessMode" | "active" | "published" | "requestStatusField" | "requestStatusMappingJson">> }
    | { type: "SET_FORM_IDENTITY"; title: string; displayTitle?: string }
    | { type: "ADD_STEP"; title: string; icon?: string }
    | { type: "REMOVE_STEP"; index: number }
    | { type: "UPDATE_STEP"; index: number; updates: Partial<BuilderStep> }
    | { type: "SYNC_FROM_HTML" };

const sync = (state: FormBuilderState): FormBuilderState => ({ ...state, htmlSource: serializeFormHtml(state), htmlDirty: false });

const replaceFieldName = (state: FormBuilderState, oldName: string, newName: string): FormBuilderState => {
    if (oldName === newName) return state;

    return {
        ...state,
        selectedField: state.selectedField === oldName ? newName : state.selectedField,
        fields: state.fields.map((field) => field.name === oldName ? { ...field, name: newName } : field),
        steps: state.steps.map((step) => ({
            ...step,
            fields: step.fields.map((name) => name === oldName ? newName : name),
        })),
        sections: state.sections.map((section) => ({
            ...section,
            fields: section.fields.map((name) => name === oldName ? newName : name),
        })),
    };
};

const formBuilderReducer = (state: FormBuilderState, action: FormBuilderAction): FormBuilderState => {
    switch (action.type) {
        case "ADD_FIELD": {
            const definition = getFieldTypeDefinition(action.fieldType);
            const label = action.label || definition?.label || action.fieldType;
            const targetStepIndex = action.stepIndex ?? state.activeStepIndex ?? (state.steps.length > 0 ? 0 : null);
            const name = uniqueName(fieldBaseName(definition?.label || action.fieldType, action.fieldType), state.fields.map((field) => field.name));
            const newField: BuilderFieldDef = {
                uid: newTranslationId(),
                name,
                type: action.fieldType,
                label,
                placeholder: "",
                required: false,
                map: "",
                source: "",
                display: "",
                valueField: "",
                referenceFilter: null,
                defaultValue: "",
                minLength: "",
                maxLength: "",
                passwordPattern: "",
                currencyCode: "",
                currencyField: "",
                accept: "",
                variant: action.fieldType === "boolean" ? "switch" : action.fieldType === "notice" ? "info" : "",
                multiple: false,
                minDate: "",
                maxDate: "",
                options: "",
                choiceItems: [],
                className: "",
                style: "",
                rules: defaultRules(),
                stepIndex: targetStepIndex,
                sectionTitle: null,
                hasRuleWarnings: false,
                ruleWarnings: [],
            };

            const fields = [...state.fields];
            const insertIndex = action.index ?? fields.length;
            fields.splice(insertIndex, 0, newField);

            const steps = state.steps.map((step, index) => {
                if (targetStepIndex !== index) return step;
                const names = step.fields.includes(name) ? step.fields : [...step.fields, name];
                return { ...step, fields: names };
            });

            return sync({ ...state, fields, steps, selectedField: name, activeStepIndex: targetStepIndex });
        }

        case "REMOVE_FIELD": {
            const fields = state.fields.filter((field) => field.name !== action.name);
            const steps = state.steps.map((step) => ({ ...step, fields: step.fields.filter((name) => name !== action.name) }));
            const sections = state.sections.map((section) => ({ ...section, fields: section.fields.filter((name) => name !== action.name) }));
            return sync({ ...state, fields, steps, sections, selectedField: state.selectedField === action.name ? null : state.selectedField });
        }

        case "MOVE_FIELD": {
            const fields = [...state.fields];
            const [moved] = fields.splice(action.fromIndex, 1);
            if (!moved) return state;

            const nextStepIndex = action.stepIndex === undefined ? moved.stepIndex : action.stepIndex;
            const movedField = { ...moved, stepIndex: nextStepIndex };
            fields.splice(action.toIndex, 0, movedField);

            const steps = state.steps.map((step, index) => {
                const withoutMoved = step.fields.filter((name) => name !== moved.name);
                if (nextStepIndex === index) {
                    return { ...step, fields: [...withoutMoved, moved.name] };
                }
                return { ...step, fields: withoutMoved };
            });

            return sync({ ...state, fields, steps, activeStepIndex: nextStepIndex ?? state.activeStepIndex });
        }

        case "UPDATE_FIELD": {
            const current = state.fields.find((field) => field.name === action.name);
            if (!current) return state;

            let nextState = state;
            let fieldName = action.name;
            const labelUpdate = action.updates.label;
            const nameSource = typeof labelUpdate === "string"
                ? (!isTranslationKey(labelUpdate) ? labelUpdate : action.labelText)
                : undefined;

            if (typeof nameSource === "string" && nameSource.trim() && !isTranslationKey(nameSource)) {
                const nextName = uniqueName(fieldBaseName(nameSource, current.type), state.fields.map((field) => field.name), current.name);
                nextState = replaceFieldName(state, current.name, nextName);
                fieldName = nextName;
            }

            const fields = nextState.fields.map((field) => field.name === fieldName ? { ...field, ...action.updates } : field);
            const previewTranslations = typeof labelUpdate === "string" && isTranslationKey(labelUpdate) && action.labelText
                ? { ...nextState.previewTranslations, [labelUpdate]: action.labelText }
                : nextState.previewTranslations;
            return sync({ ...nextState, fields, previewTranslations });
        }

        case "SET_PREVIEW_TRANSLATION":
            return { ...state, previewTranslations: { ...state.previewTranslations, [action.key]: action.text } };

        case "UPDATE_FIELD_RULES": {
            const fields = state.fields.map((field) => field.name === action.name ? { ...field, rules: { ...field.rules, ...action.rules } } : field);
            return sync({ ...state, fields });
        }

        case "SELECT_FIELD": {
            const selected = action.name ? state.fields.find((field) => field.name === action.name) : null;
            return { ...state, selectedField: action.name, activeStepIndex: selected?.stepIndex ?? state.activeStepIndex };
        }

        case "SET_ACTIVE_STEP":
            return { ...state, activeStepIndex: action.stepIndex, selectedField: null };

        case "SET_VIEW":
            if (action.view === "developer" && state.activeView === "nocode") {
                return { ...sync(state), activeView: action.view };
            }
            if (action.view === "nocode" && state.activeView === "developer" && state.htmlDirty) {
                const parsed = parseFormHtml(state.htmlSource);
                return {
                    ...state,
                    activeView: action.view,
                    fields: parsed.fields || [],
                    steps: parsed.steps || [],
                    sections: parsed.sections || [],
                    targetTable: parsed.targetTable || state.targetTable,
                    onSubmit: parsed.onSubmit || state.onSubmit,
                    successMessage: parsed.successMessage || state.successMessage,
                    successRedirect: parsed.successRedirect || state.successRedirect,
                    recordId: parsed.recordId || state.recordId,
                    eventsBinding: parsed.eventsBinding || state.eventsBinding,
                    activeStepIndex: parsed.steps?.length ? 0 : null,
                    htmlDirty: false,
                };
            }
            return { ...state, activeView: action.view };

        case "SET_HTML":
            return { ...state, htmlSource: action.html, htmlDirty: true };

        case "SET_SCRIPT_CODE":
            return { ...state, scriptCode: action.scriptCode };

        case "SYNC_FROM_HTML": {
            const parsed = parseFormHtml(state.htmlSource);
            return {
                ...state,
                fields: parsed.fields || state.fields,
                steps: parsed.steps || state.steps,
                sections: parsed.sections || state.sections,
                targetTable: parsed.targetTable || state.targetTable,
                onSubmit: parsed.onSubmit || state.onSubmit,
                successMessage: parsed.successMessage || state.successMessage,
                successRedirect: parsed.successRedirect || state.successRedirect,
                recordId: parsed.recordId || state.recordId,
                eventsBinding: parsed.eventsBinding || state.eventsBinding,
                activeStepIndex: parsed.steps?.length ? 0 : null,
                htmlDirty: false,
            };
        }

        case "SET_FORM_PROPS":
            return sync({ ...state, ...action.props });

        case "SET_FORM_IDENTITY": {
            const title = action.title || "New Service Form";
            const identityBase = action.displayTitle || title;
            return {
                ...state,
                formTitle: title,
                formName: state.identityLocked ? state.formName : normalizeBuilderToken(identityBase, "_"),
                formSlug: state.identityLocked ? state.formSlug : normalizeBuilderToken(identityBase, "-"),
            };
        }

        case "ADD_STEP": {
            if (state.steps.length === 0 && state.fields.some((field) => field.stepIndex === null)) {
                const initialStepFields = state.fields.filter((field) => field.stepIndex === null).map((field) => field.name);
                const fields = state.fields.map((field) => field.stepIndex === null ? { ...field, stepIndex: 0 } : field);
                // Intentional: turning a single-page form into a wizard keeps the existing fields in
                // Step 1 and opens an empty Step 2 to build on (a 1-step wizard would look like a no-op).
                const steps = [
                    { title: "Step 1", icon: "", fields: initialStepFields },
                    { title: "Step 2", icon: action.icon || "", fields: [] },
                ];
                return sync({ ...state, fields, steps, activeStepIndex: 1 });
            }

            const nextIndex = state.steps.length;
            const steps = [...state.steps, { title: action.title, icon: action.icon || "", fields: [] }];
            return sync({ ...state, steps, activeStepIndex: nextIndex });
        }

        case "REMOVE_STEP": {
            const remainingStepCount = Math.max(0, state.steps.length - 1);
            const targetStepIndex = remainingStepCount > 0 ? Math.max(0, action.index - 1) : null;
            const fields = state.fields.map((field) => {
                if (field.stepIndex === action.index) return { ...field, stepIndex: targetStepIndex };
                if (field.stepIndex !== null && field.stepIndex > action.index) return { ...field, stepIndex: field.stepIndex - 1 };
                return field;
            });
            const steps = state.steps
                .filter((_, index) => index !== action.index)
                .map((step, index) => ({
                    ...step,
                    fields: fields.filter((field) => field.stepIndex === index).map((field) => field.name),
                }));
            return sync({ ...state, steps, fields, activeStepIndex: steps.length ? Math.min(targetStepIndex ?? action.index, steps.length - 1) : null });
        }

        case "UPDATE_STEP": {
            const steps = state.steps.map((step, index) => index === action.index ? { ...step, ...action.updates } : step);
            return sync({ ...state, steps });
        }

        default:
            return state;
    }
};

export const useFormBuilder = (initialHtml?: string, initialScriptCode = "", metadata?: FormBuilderInitialMetadata) => {
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(
        formBuilderReducer,
        [initialHtml, initialScriptCode, metadata] as const,
        (args) => createInitialState(args[0], args[1], args[2])
    );

    const actions = useMemo(() => ({
        addField: (fieldType: PrmFieldType, index?: number, stepIndex?: number) => {
            const definition = getFieldTypeDefinition(fieldType);
            const fallback = definition?.label || fieldType;
            dispatch({ type: "ADD_FIELD", fieldType, label: String(t(builderFieldTypeLabelKey(fieldType, fallback), { defaultValue: fallback })), index, stepIndex });
        },
        removeField: (name: string) => dispatch({ type: "REMOVE_FIELD", name }),
        moveField: (fromIndex: number, toIndex: number, stepIndex?: number | null) => dispatch({ type: "MOVE_FIELD", fromIndex, toIndex, stepIndex }),
        updateField: (name: string, updates: Partial<BuilderFieldDef>, labelText?: string) => dispatch({ type: "UPDATE_FIELD", name, updates, labelText }),
        setPreviewTranslation: (key: string, text: string) => dispatch({ type: "SET_PREVIEW_TRANSLATION", key, text }),
        updateFieldRules: (name: string, rules: Partial<BuilderFieldRules>) => dispatch({ type: "UPDATE_FIELD_RULES", name, rules }),
        selectField: (name: string | null) => dispatch({ type: "SELECT_FIELD", name }),
        setActiveStep: (stepIndex: number | null) => dispatch({ type: "SET_ACTIVE_STEP", stepIndex }),
        setView: (view: "nocode" | "developer") => dispatch({ type: "SET_VIEW", view }),
        setHtml: (html: string) => dispatch({ type: "SET_HTML", html }),
        setScriptCode: (scriptCode: string) => dispatch({ type: "SET_SCRIPT_CODE", scriptCode }),
        syncFromHtml: () => dispatch({ type: "SYNC_FROM_HTML" }),
        setFormProps: (props: Partial<Pick<FormBuilderState, "targetTable" | "onSubmit" | "successMessage" | "successRedirect" | "recordId" | "eventsBinding" | "categoryId" | "accessType" | "accessRoles" | "accessGroupIds" | "publicAccessMode" | "active" | "published" | "requestStatusField" | "requestStatusMappingJson">>) => dispatch({ type: "SET_FORM_PROPS", props }),
        setFormTitle: (title: string, displayTitle?: string) => dispatch({ type: "SET_FORM_IDENTITY", title, displayTitle }),
        addStep: (title: string, icon?: string) => dispatch({ type: "ADD_STEP", title, icon }),
        removeStep: (index: number) => dispatch({ type: "REMOVE_STEP", index }),
        updateStep: (index: number, updates: Partial<BuilderStep>) => dispatch({ type: "UPDATE_STEP", index, updates }),
    }), [t]);

    const selectedFieldDef = useMemo(() => state.selectedField ? state.fields.find((field) => field.name === state.selectedField) || null : null, [state.selectedField, state.fields]);

    return { state, actions, selectedFieldDef };
};
